<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import ProgressFrame from './layout/ProgressFrame.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonOutlined from '@/shared/components/button/ButtonOutlined.vue';
import ButtonText from '@/shared/components/button/ButtonText.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import ChevronLeftSVG from '@shared/assets/icon/chevron-left.svg';
import AttentionCircleFilledSVG from '@shared/assets/icon/attention-circle-filled.svg';
import HorizontalContentRow from '../components/HorizontalContentRow.vue';
import ConnectionGroup from '../components/phase-terminal/ConnectionGroup.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { message, type FormInstance } from 'ant-design-vue';
import type { ConnectionFormData } from '../models/Connection';
import { generateDraftConnections } from '../utils/output-connections';

const { t } = useI18n();

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const deviceSpecificationStore = useDeviceSpecificationStore();

const isLoading = ref(true);
const isSubmitting = ref(false);

const formRef = ref<FormInstance>();
const formData = ref<ConnectionFormData | []>([]);

const populateFormData = () => {
  const rawDetectedConnections = commissioningDeviceStore.outputConnections;

  // build the form
  const backendConnectionsData = deviceSpecificationStore.solshareSystemSpec.connections;
  const draftConnectionsData = deviceSpecificationStore.draft.connections;
  formData.value = generateDraftConnections(
    rawDetectedConnections,
    backendConnectionsData,
    draftConnectionsData,
  );
};

onMounted(async () => {
  if (commissioningDeviceStore.terminalsStateFound) {
    populateFormData();
    isLoading.value = false;
  } else {
    router.push({ name: 'ConfigureSolshareReviewLiveConnections' });
  }
});

watch(
  formData,
  (newVal) => {
    deviceSpecificationStore.updateDraftConnections(newVal as ConnectionFormData);
  },
  { deep: true },
);

const clearData = () => {
  const rawDetectedConnections = commissioningDeviceStore.outputConnections;
  formData.value = generateDraftConnections(rawDetectedConnections, [], null);
};

const validateForm = async () => {
  try {
    await formRef.value?.validate();
  } catch {
    message.error('Invalid input submitted. Please check the details and try again.');
    return false;
  }
  return true;
};

const goBackToReviewLiveConnections = () => {
  router.push({ name: 'ConfigureSolshareReviewLiveConnections' });
};

const goToNext = async () => {
  const validForm = await validateForm();
  if (validForm) {
    try {
      isSubmitting.value = true;
      await deviceSpecificationStore.uploadConnections(serialNumber);
      router.push({ name: 'ConfigureSolshareAllocations' });
    } catch (error: Error | unknown) {
      console.log('Failed to upload connections', error);
      message.error('Failed to upload connections. Please try again.');
    } finally {
      isSubmitting.value = false;
    }
  }
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow titleTight subTitleColor="secondary">
    <template #title>{{ t('commissioning.configureConnections.pageTitle') }}</template>
    <template #subTitle>{{ serialNumber }}</template>

    <div class="instruction-text">
      <p>{{ t('commissioning.configureConnections.pageDescription') }}</p>
      <p class="note">
        <AttentionCircleFilledSVG class="note__icon" aria-hidden="true" />
        <span>NOTE: Only detected live connections can be commissioned.</span>
      </p>
    </div>

    <template v-if="isLoading">
      <LoadingSpinner />
    </template>
    <template v-else>
      <a-form ref="formRef" :model="formData" data-tn="output-connections-form">
        <template v-for="(item, index) in formData" :key="item.groupNumber">
          <HorizontalContentRow
            :firstRow="index === 0"
            :lastRow="index === formData.length - 1"
            noSidePadding
          >
            <ConnectionGroup
              :groupNumber="item.groupNumber"
              :formDataIndex="index"
              :formData="(formData as ConnectionFormData)"
              v-model:connectionGroup="item.connectionGroup"
            />
          </HorizontalContentRow>
        </template>
      </a-form>

      <div class="end-of-page">
        <ButtonPrimary
          block
          html-type="submit"
          @click="goToNext"
          :loading="isSubmitting"
          class="submit-button"
          data-tn="next-btn"
        >
          Next
          <template #iconRight>
            <ChevronRightSVG />
          </template>
        </ButtonPrimary>

        <ButtonOutlined
          block
          @click="goBackToReviewLiveConnections"
          data-tn="review-connections-btn"
        >
          Review connections
          <template #iconLeft>
            <ChevronLeftSVG />
          </template>
        </ButtonOutlined>

        <div class="start-again">
          Need to start again?
          <div>
            <ButtonText size="small" @click="clearData" data-tn="clear-data-btn">
              Clear data above
            </ButtonText>
          </div>
        </div>
      </div>
    </template>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.instruction-text {
  margin: @content-side-margin;
  font-weight: @font-weight-medium;
  text-align: center;

  .note {
    color: @color-primary;
    font-weight: @font-weight-bold;

    .note__icon {
      vertical-align: middle;
      margin-right: 0.25rem;
      width: 1.5rem;
      height: 1.5rem;
    }
  }
}

.end-of-page {
  margin: @content-side-margin;

  .submit-button {
    margin-bottom: @margin-bottom-button;
  }

  .start-again {
    margin-top: 2.4rem;
    color: @color-primary;
    text-align: center;
  }
}
</style>
